import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';

import { authHeaders, getAxiosInstance } from '../utils/api';

interface RequestInfo {
  method: AxiosRequestConfig['method'];
  url: string;
  data?: unknown;
}

export const useAxios = <T>(requestInfo: RequestInfo, isProtectedRoute = true) => {
  const axiosInstance = getAxiosInstance();

  const [data, setData] = useState<T>();
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchData = async function () {
      try {
        setLoading(true);
        const response: AxiosResponse = await axiosInstance({
          ...requestInfo,
          headers: isProtectedRoute ? authHeaders() : undefined
        });

        if (response.status === 200 || response.status === 201) {
          setData(response.data.data);
        }
        // eslint-disable-next-line no-useless-catch
      } catch (error) {
        throw error;
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [requestInfo.url]);
  return { loading, data } as { loading: boolean; data: T };
};

export const useAxiosMemoized = <T>() => {
  const [data, setData] = useState<T>({} as T);
  const [loading, setLoading] = useState(false);
  const fetchData = async function (requestInfo: RequestInfo, isProtectedRoute = true) {
    const axiosInstance = getAxiosInstance();

    try {
      setLoading(true);
      const response: AxiosResponse = await axiosInstance({
        ...requestInfo,
        headers: isProtectedRoute ? authHeaders() : undefined
      });

      if (response.status === 200 || response.status === 201) {
        // inconsistent data from BE, response.data.data & response.data
        setData(response?.data?.data ?? response?.data);
      }
      // eslint-disable-next-line no-useless-catch
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };
  const executeRequest = (requestInfo: RequestInfo, isProtectedRoute = true) => {
    if (fetchData) {
      fetchData(requestInfo, isProtectedRoute);
    }
  };
  return [executeRequest, { loading, data }] as const;
};

export default useAxios;
